import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { EXTRA_PATHS, ROLE_PATHS } from "../config/config";
import { authManager } from "../data/managers/AuthManager";
import { useAuth } from "../data/providers/AuthProvider";
import { useUserData } from "../data/providers/UserDataProvider";

const MAX_RELOAD_ATTEMPTS = 2;

const PrivateRoute: React.FC = () => {
  const { user, loading } = useAuth();
  const data = useUserData();
  const location = useLocation();
  const navigate = useNavigate();
  const failedAttemptsRef = useRef(0);
  const [failedAttempts, setFailedAttempts] = useState(0);

  const resetAttempts = () => {
    failedAttemptsRef.current = 0;
    setFailedAttempts(0);
  };

  const forceLogout = async () => {
    resetAttempts();
    data?.clearUserData();
    await authManager.logout();
    navigate("/login?session=invalid", { replace: true });
  };

  const reloadUserDataWithGuard = async () => {
    const isSuccess = await data?.reloadUserData();

    if (isSuccess) {
      resetAttempts();
      return;
    }

    const nextAttempts = failedAttemptsRef.current + 1;
    failedAttemptsRef.current = nextAttempts;
    setFailedAttempts(nextAttempts);

    if (nextAttempts >= MAX_RELOAD_ATTEMPTS) {
      await forceLogout();
    }
  };

  useEffect(() => {
    if (user) {
      void reloadUserDataWithGuard();
      return;
    }
    resetAttempts();
    data?.clearUserData();
  }, [user]);

  if (loading || data?.loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (data?.hasError) {
    const retriesLeft = Math.max(0, MAX_RELOAD_ATTEMPTS - failedAttempts);
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          No pudimos validar tu sesion en este momento.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Te queda {retriesLeft} reintento{retriesLeft === 1 ? "" : "s"}.
        </Typography>
        <Button
          variant="contained"
          onClick={() => void reloadUserDataWithGuard()}
        >
          Reintentar
        </Button>
        <Button color="inherit" onClick={() => void forceLogout()}>
          Cerrar sesion
        </Button>
      </Box>
    );
  }

  if (!data?.data) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const role = data.data.role ?? "USER";

  if (role === "USER" && location.pathname === "/dashboard") {
    return <Navigate to="/dashboard/plan" replace />;
  } else if (role === "ADMIN" && location.pathname === "/dashboard") {
    return <Navigate to="/dashboard/admin/users" replace />;
  }

  const allowedRoutes = [
    ...(ROLE_PATHS[role as keyof typeof ROLE_PATHS] || []),
    ...(EXTRA_PATHS[role as keyof typeof EXTRA_PATHS] || []),
  ];

  const currentPath = location.pathname;
  const hasAccess = allowedRoutes.some((path) => currentPath.startsWith(path));

  if (!hasAccess) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          textAlign: "center",
          bgcolor: "background.default",
          p: 4,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, mb: 1, color: "text.primary" }}
        >
          🚫 Acceso denegado
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          No tienes permiso para acceder a esta sección.
        </Typography>
        <Button
          variant="contained"
          disableElevation
          onClick={() => {
            const redirectTo =
              role === "ADMIN" ? "/dashboard" : "/dashboard/plan";
            navigate(redirectTo, { replace: true });
          }}
        >
          Ir a mi panel
        </Button>
      </Box>
    );
  }

  return <Outlet />;
};

export default PrivateRoute;
